import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PortalPageHeader } from "@/components/portal/PortalLayout";
import PricingMatrix from "@/components/portal/PricingMatrix";
import { toast } from "sonner";
import { Cog, ChevronDown, ChevronRight, Save, Ban, CheckCircle2 } from "lucide-react";

/**
 * Partner Portal → Categorias de Veículos (transfers dinâmicos)
 * O parceiro pode sobrescrever a tarifa (base €, €/km, €/min) por categoria e temporada.
 */
export default function PartnerVehicleCategories() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    try {
      const { data: d } = await api.get("/partner/vehicle-categories");
      setData(d);
      const draftMap = {};
      d.categories.forEach((c) => { draftMap[c.key] = c._overrides || {}; });
      setDrafts(draftMap);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const setDraft = (key, patch) => setDrafts((d) => ({ ...d, [key]: { ...(d[key] || {}), ...patch } }));
  const setDraftOverrides = (key, newOverrides) => setDrafts((d) => ({ ...d, [key]: { ...newOverrides, available: (d[key] || {}).available } }));

  const save = async (key) => {
    setSavingKey(key);
    try {
      const payload = drafts[key] || {};
      await api.put(`/partner/vehicle-categories/${key}/pricing`, payload);
      toast.success("Tarifa actualizada");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingKey(null); }
  };

  if (!data) return <div className="p-10 text-sm text-slate-500">A carregar...</div>;

  return (
    <div className="p-8 max-w-[1400px]" data-testid="partner-vehicle-categories-page">
      <PortalPageHeader
        eyebrow="Catálogo"
        title="Categorias de Veículos"
        description="Configure a sua tarifa de transfer dinâmico (base + €/km + €/min) por categoria e temporada. Aplica-se quando o cliente escolhe origem+destino livres. Vazio = usa tarifa do admin. Pode definir preços próprios mesmo em categorias sem base admin."
      />

      <div className="space-y-4">
        {data.categories.length === 0 && (
          <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">Nenhuma categoria disponível.</div>
        )}
        {data.categories.map((c) => {
          const draft = drafts[c.key] || {};
          const isExpanded = expanded === c.key;
          const disabled = draft.available === false;
          const baseAdmin = {
            base_price_eur: c.base_price_eur,
            price_per_km: c.price_per_km,
            price_per_minute: c.price_per_minute,
          };

          return (
            <div key={c.key} className={`border ${disabled ? "border-slate-300 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`} data-testid={`ptr-cat-${c.key}`}>
              <button
                type="button"
                onClick={() => setExpanded(isExpanded ? null : c.key)}
                className="w-full flex items-center gap-4 p-4 text-left hover:bg-slate-50"
                data-testid={`ptr-cat-toggle-${c.key}`}
              >
                {c.image_url ? (
                  <img src={c.image_url} alt="" className="w-16 h-16 object-cover border border-slate-200" />
                ) : (
                  <div className="w-16 h-16 bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-300">
                    <Cog className="w-6 h-6" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg text-[#0a0f1e] flex items-center gap-2">
                    {c.label}
                    {disabled && <span className="text-[10px] uppercase text-red-600 font-bold flex items-center gap-1"><Ban className="w-3 h-3" /> Desactivada</span>}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{c.key} · {c.capacity_pax} pax · {c.capacity_bags} malas</div>
                </div>
                <div className="text-right shrink-0 font-mono text-xs text-slate-600">
                  <div>Base €{Number(c.base_price_eur || 0).toFixed(0)}</div>
                  <div>+ €{Number(c.price_per_km || 0).toFixed(2)}/km</div>
                  <div>+ €{Number(c.price_per_minute || 0).toFixed(2)}/min</div>
                </div>
                {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
              </button>

              {isExpanded && (
                <div className="border-t border-slate-100 p-5 space-y-4 bg-slate-50/30">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setDraft(c.key, { available: !disabled ? false : true })}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest font-bold border ${disabled ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-red-400 text-red-600"}`}
                      data-testid={`ptr-cat-availability-${c.key}`}
                    >
                      {disabled ? <><CheckCircle2 className="w-3.5 h-3.5" /> Reactivar categoria</> : <><Ban className="w-3.5 h-3.5" /> Desactivar categoria</>}
                    </button>
                  </div>

                  {!disabled && (
                    <PricingMatrix
                      basePrices={{}}
                      baseAdmin={baseAdmin}
                      seasons={data.seasons}
                      priceFields={["base_price_eur", "price_per_km", "price_per_minute"]}
                      zoneKey={null}
                      overrides={draft}
                      onChange={(o) => setDraftOverrides(c.key, o)}
                      testIdPrefix={`ptr-cat-${c.key}`}
                    />
                  )}

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => save(c.key)}
                      disabled={savingKey === c.key}
                      className="inline-flex items-center gap-2 h-10 px-5 bg-[#f59e0b] text-white font-bold text-xs uppercase tracking-wide hover:bg-[#d97706] disabled:opacity-50"
                      data-testid={`ptr-cat-save-${c.key}`}
                    >
                      <Save className="w-3.5 h-3.5" /> {savingKey === c.key ? "A guardar..." : "Guardar tarifa"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
